import { Color, Vector3 } from 'three';

/**
 * Day / night cycle for Street 60.
 *
 * `computeTimeOfDay(t)` maps a single normalised cycle position (`t` in [0,1):
 * 0.0 = midnight, 0.25 = dawn, 0.5 = noon, 0.75 = dusk, 1.0 wraps to midnight)
 * onto every lighting parameter the renderer needs. The Environment advances `t`
 * over `config.timeOfDay.cycleSeconds` and re-applies the result each frame, so
 * the sky, sun, moon, fog, ambient and exposure all ease together between day
 * and night.
 */

function smoothstep(edge0, edge1, x) {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

// Keyframe palettes. Each is a full set of interpolation targets.
const PALETTES = {
  night: {
    topColor: 0x070b16,
    horizonColor: 0x12182b,
    groundColor: 0x05070d,
    sunColor: 0x9fb4e0, // moon-glow colour, used by the sky shader at night
    fogColor: 0x0a0e1a,
    fogNear: 28,
    fogFar: 250,
    hemiSky: 0x1a2740,
    hemiGround: 0x05070d,
    hemiIntensity: 0.16,
    sunIntensity: 0.0,
    moonIntensity: 0.85,
    exposure: 0.6,
  },
  dawn: {
    topColor: 0x6f97c4,
    horizonColor: 0xf0a868,
    groundColor: 0x6e5a44,
    sunColor: 0xffd9a0,
    fogColor: 0xcdb491,
    fogNear: 50,
    fogFar: 420,
    hemiSky: 0xbcd2ee,
    hemiGround: 0x6e5a44,
    hemiIntensity: 0.4,
    sunIntensity: 1.7,
    moonIntensity: 0.15,
    exposure: 0.95,
  },
  day: {
    topColor: 0x3d76ad,
    horizonColor: 0xe8cfa8,
    groundColor: 0xae9570,
    sunColor: 0xfff1cf,
    fogColor: 0xd9c2a0,
    fogNear: 60,
    fogFar: 460,
    hemiSky: 0xbdd6f2,
    hemiGround: 0xb39470,
    hemiIntensity: 0.45,
    sunIntensity: 3.25,
    moonIntensity: 0.0,
    exposure: 1.02,
  },
  dusk: {
    topColor: 0x3f5286,
    horizonColor: 0xe07a3c,
    groundColor: 0x554637,
    sunColor: 0xffb070,
    fogColor: 0xc6a98c,
    fogNear: 48,
    fogFar: 380,
    hemiSky: 0xa9b6d6,
    hemiGround: 0x554637,
    hemiIntensity: 0.36,
    sunIntensity: 1.4,
    moonIntensity: 0.2,
    exposure: 0.9,
  },
};

// Ordered around the cycle; the last entry repeats the first for a clean wrap.
const KEYFRAMES = [
  { t: 0.0, p: PALETTES.night },
  { t: 0.25, p: PALETTES.dawn },
  { t: 0.5, p: PALETTES.day },
  { t: 0.75, p: PALETTES.dusk },
  { t: 1.0, p: PALETTES.night },
];

const NUMERIC_KEYS = [
  'fogNear', 'fogFar', 'hemiIntensity', 'sunIntensity', 'moonIntensity', 'exposure',
];
const COLOR_KEYS = [
  'topColor', 'horizonColor', 'groundColor', 'sunColor', 'fogColor', 'hemiSky', 'hemiGround',
];

const MOON_COLOR = 0x9fb4e0;

// Reused scratch colours so compute() allocates nothing but two Vector3s/frame.
const _a = new Color();
const _b = new Color();
const _out = new Color();

function lerpHex(hexA, hexB, f) {
  _a.set(hexA);
  _b.set(hexB);
  _out.copy(_a).lerp(_b, f);
  return _out.getHex();
}

// Sun and moon share one arc: the sun rides it by day, the moon by night.
function directions(t) {
  const sunAngle = (t - 0.25) * Math.PI * 2; // 0 at dawn, PI/2 at noon, PI at dusk
  const elev = Math.sin(sunAngle);
  const h = Math.sqrt(Math.max(0, 1 - elev * elev));
  const baseX = -0.5;
  const baseZ = 0.34;
  const bl = Math.hypot(baseX, baseZ);
  const sunDir = new Vector3((baseX / bl) * h, elev, (baseZ / bl) * h).normalize();

  const moonElev = -elev;
  const hm = Math.sqrt(Math.max(0, 1 - moonElev * moonElev));
  const moonDir = new Vector3((baseX / bl) * hm, moonElev, (baseZ / bl) * hm).normalize();

  return { sunDir, moonDir };
}

export function computeTimeOfDay(t) {
  t = ((t % 1) + 1) % 1;

  let a = KEYFRAMES[0];
  let b = KEYFRAMES[KEYFRAMES.length - 1];
  for (let i = 0; i < KEYFRAMES.length - 1; i += 1) {
    if (t >= KEYFRAMES[i].t && t <= KEYFRAMES[i + 1].t) {
      a = KEYFRAMES[i];
      b = KEYFRAMES[i + 1];
      break;
    }
  }

  const span = b.t - a.t || 1;
  const f = smoothstep(0, 1, (t - a.t) / span);

  const out = {};
  for (const k of NUMERIC_KEYS) out[k] = a.p[k] + (b.p[k] - a.p[k]) * f;
  for (const k of COLOR_KEYS) out[k] = lerpHex(a.p[k], b.p[k], f);

  const { sunDir, moonDir } = directions(t);
  out.sunDirection = sunDir;
  out.moonDirection = moonDir;
  out.moonColor = MOON_COLOR;
  // 0 in full daylight, 1 in deep night, smooth across dusk/dawn.
  out.nightFactor = 1 - smoothstep(-0.08, 0.22, sunDir.y);

  return out;
}
